/**
 * The single place that decides which credential a request carries.
 *
 * Five modules used to read `cortex_access_token` independently: authFetch,
 * proxyFetch, EnableProvider's enableFetch, ProjectContext, AuthContext and
 * AppShellContext. With impersonation, any one of them that kept its own
 * resolution would serve the operator's REAL identity on that surface — some
 * panels showing the partner, some showing you, which is both the likeliest
 * failure and the hardest to notice.
 *
 * Precedence: impersonation > share > access.
 *
 * The impersonation token lives in sessionStorage, so it is PER TAB: the
 * operator's own session survives untouched in their other tabs. Overwriting
 * localStorage would hijack every tab on the origin and discard it.
 */

const TOKEN_KEY = "cortex_access_token";
const SHARE_TOKEN_KEY = "cortex_share_access_token";
export const IMPERSONATION_TOKEN_KEY = "cortex_impersonation_token";

export type AuthTokenKind = "impersonation" | "share" | "access" | "none";

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function getImpersonationToken(): string | null {
  return readSession(IMPERSONATION_TOKEN_KEY);
}

export function setImpersonationToken(token: string): void {
  try {
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
  } catch {
    /* a session that cannot be stored simply does not start */
  }
}

export function clearImpersonationToken(): void {
  try {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** True while browsing under a share-prefixed URL. Inside that surface the
 * share-session JWT beats a regular login Bearer, so an already-logged-in tab
 * sees the share's scoped slice rather than its own access. */
function isShareSurface(): boolean {
  try {
    return /^\/share\/[^/]+/.test(window.location.pathname);
  } catch {
    return false;
  }
}

export function resolveAuthToken(): { token: string | null; kind: AuthTokenKind } {
  const impersonation = getImpersonationToken();
  if (impersonation) return { token: impersonation, kind: "impersonation" };

  const share = readSession(SHARE_TOKEN_KEY);
  const access = readLocal(TOKEN_KEY);
  if (share && (isShareSurface() || !access)) return { token: share, kind: "share" };
  if (access) return { token: access, kind: "access" };
  return { token: null, kind: "none" };
}
